import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../constants/svgIcon';
import { getGameTypeDetails } from '../../../constants/Games/gameType';
import { Profile } from '../../../models/UserStore';
import { AvatarNode, SvgIcon } from '../../Molecules';

import './GameHud.css';

export type GameHudProps = {
  profiles: Profile[];
  gameType: string;
  scores: number[];
  elapsedTime: string;
  instruction?: string;
  onClickExpandInstruction?: () => void;
};

export const GameHud = memo(
  ({
    profiles,
    gameType,
    scores,
    elapsedTime,
    instruction,
    onClickExpandInstruction,
  }: GameHudProps) => {
    const profilesLength = profiles.length;
    const scoresLength = scores.length;
    const total = scores.reduce((acc, cur) => (acc += cur), 0);

    return (
      <div className="game-hud-container">
        <div className="game-hud">
          {profilesLength === scoresLength && profilesLength === 1 ? (
            <TutorialHud
              profiles={profiles}
              gameType={gameType}
              scores={scores}
              elapsedTime={elapsedTime}
              instruction={instruction}
            />
          ) : null}
          {profilesLength === scoresLength && profilesLength === 2 ? (
            <MemoryGameHud
              profiles={profiles}
              gameType={gameType}
              scores={scores}
              elapsedTime={elapsedTime}
              instruction={instruction}
            />
          ) : null}
          <ScoresBar scores={scores} total={total} />
          <ScoreBoards scores={scores} total={total} />
        </div>
        {instruction && (
          <div className="question-container">
            {/* <TTSPlayer /> */}

            <p className="question-text">{instruction}</p>

            {onClickExpandInstruction && (
              <div
                className="expand-question-button"
                onClick={onClickExpandInstruction}
              >
                <SvgIcon
                  name={SVGIconType.Arrow}
                  width={20}
                  height={20}
                  stroke="#808080"
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

const Timer = memo(({ elapsedTime }: { elapsedTime: string }) => {
  return (
    <div className="timer">
      {/* <img className="icon" src={Icons[IconType.TimeLeft]} /> */}
      <SvgIcon name={SVGIconType.Timer} width={20} height={20} />
      <p className="text">{elapsedTime}</p>
    </div>
  );
});

const TutorialHud = memo(
  ({ profiles, gameType, scores, elapsedTime }: GameHudProps) => {
    const { t } = useTranslation();
    const total = scores.reduce((acc, cur) => (acc += cur), 0);
    const { color: player1ScoreColor } = getScoreColor(
      scores[0]!,
      total,
      '#85CC13',
    );
    return (
      <div className="tutorial-hud">
        <div
          className="avatar-container"
          style={{
            paddingLeft: 96 / 1.5,
          }}
        >
          <AvatarNode
            avatar={{
              ...profiles[0]!.avatar,
              userId: profiles[0]!.userId,
              source:
                profiles[0]!.profilePicture &&
                profiles[0]!.profilePicture.publicUrls?.small
                  ? {
                      uri: profiles[0]!.profilePicture.publicUrls.small,
                    }
                  : '',
            }}
            size={44}
          />

          <div
            className="score-tag"
            style={{
              marginRight: 96,
              backgroundColor: player1ScoreColor,
            }}
          >
            {scores[0]}
          </div>
        </div>
        <div className="tutorial-info">
          <p className="tutorial-info-mode-text">{t('Gamified Tutorial')}</p>
          <p className="tutorial-info-game-type-text">
            {t(getGameTypeDetails(gameType).displayName)}
          </p>
          <div style={{ display: 'block' }}>
            <Timer elapsedTime={elapsedTime} />
          </div>
        </div>
      </div>
    );
  },
);

const MemoryGameHud = memo(
  ({ profiles, scores, elapsedTime }: GameHudProps) => {
    const total = scores.reduce((acc, cur) => (acc += cur), 0);

    const { color: player1ScoreColor } = getScoreColor(
      scores[0]!,
      total,
      '#85CC13',
    );
    const { color: player2ScoreColor } = getScoreColor(
      scores[1]!,
      total,
      '#BF54C9',
    );

    return (
      <div className="inner-hud">
        <div className="avatar-group">
          <div
            className="avatar-container"
            style={{
              paddingLeft: 96 / 1.5,
            }}
          >
            <AvatarNode
              avatar={{
                ...profiles[0]!.avatar,
                userId: profiles[0]!.userId,
                source:
                  profiles[0]!.profilePicture &&
                  profiles[0]!.profilePicture.publicUrls?.small
                    ? {
                        uri: profiles[0]!.profilePicture.publicUrls.small,
                      }
                    : '',
              }}
              size={44}
            />
            <div
              className="score-tag"
              style={{
                marginRight: 96,
                backgroundColor: player1ScoreColor,
              }}
            >
              {scores[0]}
            </div>
          </div>

          <div
            className="avatar-container"
            style={{
              paddingRight: 96 / 1.5,
            }}
          >
            <AvatarNode
              avatar={{
                ...profiles[1]!.avatar,
                userId: profiles[1]!.userId,
                source:
                  profiles[1]!.profilePicture &&
                  profiles[1]!.profilePicture.publicUrls?.small
                    ? {
                        uri: profiles[1]!.profilePicture.publicUrls.small,
                      }
                    : '',
              }}
              size={44}
            />
            <div
              className="score-tag"
              style={{
                marginLeft: 96,
                backgroundColor: player2ScoreColor,
              }}
            >
              {scores[1]}
            </div>
          </div>
        </div>
        <div className="memory-game-hub-timer">
          <Timer elapsedTime={elapsedTime} />
        </div>
        <div className="vs-flag">
          <p className="vs-flag-label">VS</p>
        </div>
      </div>
    );
  },
);

const ScoresBar = memo(
  ({ scores, total }: { scores: number[]; total: number }) => {
    const colors = ['#85CC13', '#BF54C9']; // Define the colors
    return (
      <div className="scores-bar">
        {scores.map((score, index) => {
          const mainColor = colors[index % colors.length] ?? '#85CC13';
          const color =
            score >= 50 ? mainColor : score > 20 ? '#f9a623' : '#eb4d4d';
          return (
            <ScoreBar key={index} score={score} total={total} color={color} />
          );
        })}
        {scores.length > 1 && <div className="scores-bar-center-indicator" />}
      </div>
    );
  },
);

const getScoreColor = (
  score: number,
  total: number,
  mainColor: string = '#85cc12',
) => {
  const color = score >= 50 ? mainColor : score > 20 ? '#f9a623' : '#eb4d4d';
  const flex = (score / (total == 0 ? 1 : total)) * 100;
  return { color, flex };
};

const ScoreBar = memo(
  ({
    score,
    total,
    color,
  }: {
    score: number;
    total: number;
    color: string;
  }) => {
    const { flex } = getScoreColor(score, total);
    return (
      <div
        className="score-bar"
        style={{
          backgroundColor: color,
          flex: flex,
        }}
      />
    );
  },
);

const ScoreBoards = memo(
  ({ scores, total }: { scores: number[]; total: number }) => {
    return (
      <div className="score-boards">
        {scores.map((score, index) => (
          <ScoreBoard key={index} score={score} total={total} />
        ))}
      </div>
    );
  },
);

const ScoreBoard = memo(
  ({ score, total }: { score: number; total: number }) => {
    const { color, flex } = getScoreColor(score, total);
    return (
      <div className="score-board" style={{ flex: flex }}>
        {/* <p className="score" style={{ backgroundColor: color }}>
          {score}
        </p> */}
        <div className="score-middle-line" />
      </div>
    );
  },
);
